import { ReactNode, useState, useCallback, memo, Suspense, lazy } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { StaffChat } from "@/components/Communication/StaffChat";
import { useFreezerOutTimeMonitor } from "@/hooks/useFreezerOutTimeMonitor";

// Lazy load del Sidebar para mejor rendimiento inicial
const Sidebar = lazy(() => import("./Sidebar").then(m => ({ default: m.Sidebar })));

// Fallback simple mientras carga el Sidebar
const SidebarFallback = () => (
  <div className="w-64 bg-card border-r border-border h-full animate-pulse" />
);

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout = memo(({ children }: DashboardLayoutProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Monitor out-time de paquetes de congelador (solo supervisores/admins)
  useFreezerOutTimeMonitor();

  const openMenu = useCallback(() => setMobileMenuOpen(true), []);
  const closeMenu = useCallback(() => setMobileMenuOpen(false), []);

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <Suspense fallback={<SidebarFallback />}>
          <Sidebar />
        </Suspense>
      </div>

      {/* Mobile Sidebar - Sheet optimizado */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent 
          side="left" 
          className="p-0 w-[280px] max-w-[85vw]"
        >
          <Suspense fallback={<SidebarFallback />}>
            <Sidebar onNavigate={closeMenu} />
          </Suspense>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header onMenuClick={openMenu} />
        <main
          className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children}
        </main>
        <Footer />
      </div>

      {/* Chat flotantes */}
      <StaffChat />
    </div>
  );
});

DashboardLayout.displayName = 'DashboardLayout';
